import { Point } from "./schemas";
import {
  MindmapNodeDirection,
  MindmapNodeOrientation,
  MindmapOrgChartNodeElement,
  mindmapOrgChartNodeSchema,
} from "./schemas/mindmap-org-chart";
import { createElementId, getUnixTimestampUTC } from "../util/utils";
import consts from "../consts";
import { IntegrationType } from "../integrations/integration";
import {
  generateDepartmentId,
  isDepartmentAsSource,
  SourceNodeType,
} from "frontend/canvas-designer-new/elements/org-chart/orgchart-utils";

export const Padding = {
  padding_vertical: 14,
  padding_horizontal: 10,
  padding_between_lines: 5,
  minNodeSize: { width: 180, height: 100 },
  padding_seperator: 10,
  padding_connector_child: 16,
};

export const DEFAULT_COLOR = "#7549F5";
export const TEXT_NODE_COLOR = "#EAE4FE";
export const TEXT_NODE_HEIGHT = 150;
export const ORGCHART_NODE_DEFAULT_WIDTH = 200;
export const ORGCHART_NODE_DEFAULT_HEIGHT = 50;
export const ORGCHART_FILTER_MARKER_RADIUS = 10;
export const ORGCHART_FIELD_HEIGHT = 12;
export const NODE_DEFAULT_FIELDS_COUNT = 3;
export const TEXT_FIELD_HEIGHT = 15;
export const NODE_ADDITIONAL_HEIGHT = 5;

// this function is used to create an object that will be saved to the db
export function placeMindMapOrgChartNode(
  integrationId: string,
  integrationType: IntegrationType,
  configuration: any,
  { x, y }: Point,
  current_item: any,
  sourceType: SourceNodeType,
  shouldEnableDepartmentAsRoot: boolean,
  rootId?: string,
  parentId?: string
) {
  const id = createElementId();
  const color = DEFAULT_COLOR;

  const excludedColumns = [
    configuration.columnMapping.jobTitle,
    configuration.columnMapping.selfId,
    configuration.columnMapping.parentId,
    "name",
  ];
  configuration.columns = configuration.columns.filter((col: any) => !excludedColumns.includes(col.id));

  const isDepartmentSource = isDepartmentAsSource(sourceType, shouldEnableDepartmentAsRoot);

  const sourceId = isDepartmentSource ? generateDepartmentId(current_item.columnId, current_item.id) : rootId;

  return {
    id,
    node: mindmapOrgChartNodeSchema.parse({
      type: consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART,
      x,
      y,
      width: ORGCHART_NODE_DEFAULT_WIDTH,
      height: ORGCHART_NODE_DEFAULT_HEIGHT,
      nodes: [],
      orientation: MindmapNodeOrientation.HORIZONTAL,
      direction: MindmapNodeDirection.TRAILING,
      zIndexLastChangeTime: getUnixTimestampUTC(),
      rootId: sourceId ?? current_item.id, // if rootId is not provided, this is the root
      selfId: isDepartmentSource ? sourceId : current_item.id, // if rootId is not provided, this is the root
      parentId: parentId,
      sortIndex: 0,
      collapsed: false,
      integrationId,
      integrationType,
      configuration,
      color: color,
      text: "",
      job_title: "",
      expandedNodes: [current_item.id],
      selectedFields: [],
      columnValues: [],
      selectedColorFieldId: undefined,
    }) as MindmapOrgChartNodeElement,
  };
}

// this function is used to create an object that will be saved to the db
export function placeMindMapOrgChart(
  integrationId: string,
  integrationType: IntegrationType,
  configuration: any,
  { x, y }: Point,
  all_data: any,
  sourceType: SourceNodeType,
  shouldEnableDepartmentAsRoot: boolean,
  rootId?: string,
  parentId?: string
) {
  const current_item = all_data.find((item: any) => item.id == configuration.itemId) ?? {
    id: configuration.itemId,
  };

  const parent_element = placeMindMapOrgChartNode(
    integrationId,
    integrationType,
    configuration,
    { x, y },
    current_item,
    sourceType,
    shouldEnableDepartmentAsRoot,
    rootId,
    parentId
  );

  const isDepartmentSource = isDepartmentAsSource(sourceType, shouldEnableDepartmentAsRoot);

  if (!isDepartmentSource) {
    parent_element.node.childrenColumnValues = generateColumnValues(all_data, configuration.itemId, {})[
      configuration.itemId
    ];
  }

  const data = [
    {
      id: parent_element.id,
      type: parent_element.node.type,
      element: parent_element.node,
    },
  ];

  return data;
}

type ColumnItem =
  | { id: string; title: string; settings: any } & (
      | { type: "dropdown"; value: string }
      | {
          type: "status";
          value: { text: string };
        }
    );

export function generateColumnValues(
  all_data: any,
  current_element_id: string,
  result: { [key: string]: { [key: string]: string[] } }
) {
  const current_element = all_data.find((item: { id: string }) => item.id === current_element_id);
  if (!current_element) return result;

  result[current_element.id] = {};

  if (!current_element.columnValues) return result;

  current_element.columnValues.map((col: ColumnItem) => {
    let values: string[] = [];

    if (col.type === "status") {
      values = [col.value.text];
    }
    if (col.type === "dropdown") {
      values = col.value.split(", ");
    }
    if (values.length && col.id && !result[current_element.id][col.id]) {
      result[current_element.id][col.id] = [];
    }
    values.forEach((colVal: string) => {
      if (colVal && !result[current_element.id][col.id].includes(colVal)) {
        result[current_element.id][col.id].push(colVal);
      }
    });
  });
  const children = all_data.filter((child: any) => child.parent == current_element.self && child.parent != child.self);
  children.map((child: any) => {
    result = generateColumnValues(all_data, child.id, result);
  });

  children.map((child: any) => {
    const columnData = result[child.id];

    for (const key in columnData) {
      if (result[current_element.id][key]) {
        result[current_element.id][key] = Array.from(new Set([...result[current_element.id][key], ...columnData[key]]));
      } else {
        result[current_element.id][key] = columnData[key];
      }
    }
  });
  return result;
}
